/**
 * Канал нашего номера (номер × площадка): кто из сотрудников на нём сейчас и кто был последним.
 * Одна ячейка на «Расширение» → «Кто онлайн» → «По номерам» и в окне номера в «Чатах».
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import type { ExtPhoneChannel, ExtPhoneDeed, ExtPhoneDeeds, ExtPhoneVersion, ExtTimeStatus } from "@/lib/extension";

export function ago(seconds: number): string {
  if (seconds < 90) return "только что";
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин назад`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} ч назад`;
  return `${Math.round(seconds / 86400)} дн назад`;
}

export const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

export function Dot({ tone }: { tone: "ok" | "warn" | "err" | "off" }) {
  const cls = { ok: "bg-ok", warn: "bg-warn", err: "bg-err", off: "bg-fg-faint" }[tone];
  return <span className={`inline-block size-2 flex-none rounded-full ${cls}`} />;
}

function Deed({ label, deed }: { label: string; deed: ExtPhoneDeed | null }) {
  return (
    <span className="text-fg-muted">
      {label}:{" "}
      {deed ? (
        <>
          {deed.chat_id ? (
            <Link to={`/chats/${deed.chat_id}`} className="text-fg hover:underline" title="открыть в «Чатах»">
              {deed.name}
            </Link>
          ) : (
            <span className="text-fg">{deed.name}</span>
          )}
          <span className="tabular-nums"> · {when(deed.at)}</span>
        </>
      ) : (
        <span className="text-fg-faint">нет за 7 дней</span>
      )}
    </span>
  );
}

/** Что сотрудник делал на этом канале; без права на журналы расширения сервер этого не отдаёт. */
function Deeds({ of }: { of: ExtPhoneDeeds }) {
  if (of.dialog === undefined || of.sent === undefined) return null;
  return (
    <>
      <Deed label="диалог" deed={of.dialog} />
      <Deed label="отправка" deed={of.sent} />
    </>
  );
}

const STATUS_TONE = { work: "text-ok", break: "text-warn", away: "text-err" };

/** С какого момента статус: сегодня — время, раньше — день и время, прошлые годы — дата. */
function since(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  if (d.getFullYear() === now.getFullYear())
    return `${d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })} ${time}`;
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** Статус сотрудника в учёте времени CRM: работает, на перерыве или ушёл — и с какого момента. */
function TimeStatus({ status }: { status: ExtTimeStatus | null | undefined }) {
  if (!status) return null;
  return (
    <span className="text-fg-muted" title="статус сотрудника в учёте времени CRM">
      статус: <span className={`font-medium ${STATUS_TONE[status.kind]}`}>{status.name}</span>
      {status.since && <span className="tabular-nums"> · с {since(status.since)}</span>}
    </span>
  );
}

/** Версия расширения в браузере, где у сотрудника открыт этот канал; старая — красным. */
function Version({ of }: { of: ExtPhoneVersion }) {
  if (!of.version) return null;
  return (
    <span className="text-fg-muted" title="версия расширения в браузере, где открыт этот канал">
      расширение: <span className={`tabular-nums ${of.fresh ? "text-fg" : "font-medium text-err"}`}>{of.version}</span>
      {!of.fresh && <span className="text-err"> · старая</span>}
    </span>
  );
}

/** Расширение на вкладках канала падает с ошибками (телеметрия `error` за час); ошибок нет — строки нет. */
function Errors({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <span className="font-medium text-err" title="ошибки кода расширения на вкладках этого канала за последний час; подробности — «Расширение» → «Журнал»">
      ошибки расширения за час: {n}
    </span>
  );
}

/**
 * Почему канал у сотрудника не отмечается, хотя этот же номер он держит в другом канале. Отметки
 * шлёт расширение с самой вкладки: не запускалось на ней — вкладки нет либо она открыта с прежним
 * расширением; запускалось и замолчало — вкладку закрыли или она зависла.
 */
function Silent({ platform, of }: { platform: string; of: ExtPhoneChannel["beside"][number] }) {
  if (of.tab)
    return (
      <span className="text-warn" title={`Расширение на вкладке ${platform} в этом браузере работало и перестало слать отметки: вкладку закрыли, либо она зависла`}>
        вкладка {platform} в этом браузере молчит с {when(of.tab.seen)}: закрыта или зависла
      </span>
    );
  return (
    <span
      className="text-warn"
      title={`С вкладки ${platform} в этом браузере расширение ${of.version} не прислало ни одной отметки: вкладка не открыта, либо открыта до установки расширения и не обновлена — в ней остался прежний, уже не работающий скрипт. Ошибкой это не считается: скрипт на вкладке просто не запускался`}
    >
      расширение на вкладке {platform} в этом браузере не запускалось: вкладка не открыта или не обновлена (F5)
    </span>
  );
}

const BRIDGE: Record<NonNullable<ExtPhoneChannel["bridge"]>["state"], [string, string]> = {
  ok: ["на связи", "text-ok"],
  silent: ["молчит", "text-err"],
  nologin: ["нет входа", "text-err"],
  off: ["приём выключен", "text-warn"],
  unused: ["не используем", "text-fg-faint"],
};

// Ответ в канале не старше — в канале отвечают, хоть вкладка с расширением ни у кого и не открыта.
const REPLYING = 900;
const replying = (c: ExtPhoneChannel) => !!c.replied && c.replied.age <= REPLYING;

/** Клиенты ждут ответа — красный лейбл над ячейкой; никто не ждёт — лейбла нет. */
function Waiting({ w }: { w: ExtPhoneChannel["waiting"] }) {
  if (w.count === 0) return null;
  return (
    <Link
      to={`/chats/${w.chat_id}`}
      className="self-start rounded bg-err px-1.5 py-0.5 text-[11px] font-medium text-white hover:opacity-90"
      title="диалоги канала в «Чатах», где клиент ждёт ответа (за сутки); открыть самый давний"
    >
      без ответа: {w.count} · ждёт {w.minutes ?? 0} мин
    </Link>
  );
}

/**
 * Мост канала не несёт сообщения — красный лейбл над ячейкой: сотрудник «в фоне» зелёным, а
 * неоткрытые диалоги в «Чаты» не идут (22.10–29.10.26 так стоял WhatsApp «Шоурума»).
 */
function BridgeDown({ b }: { b: ExtPhoneChannel["bridge"] }) {
  if (!b || (b.state !== "nologin" && b.state !== "silent")) return null;
  return (
    <span
      className="self-start rounded bg-err px-1.5 py-0.5 text-[11px] font-medium text-white"
      title={`Мост в служебном браузере ${b.name} не несёт в «Чаты» неоткрытые диалоги. Нет входа — войти телефоном с этим номером (QR или код по номеру)`}
    >
      {b.state === "nologin" ? "мост: нет входа — нужен QR" : "мост молчит"} · сообщения не идут в «Чаты»
    </span>
  );
}

/** Что по каналу видно без сотрудника: когда отвечали и жива ли вкладка моста. */
function Facts({ c }: { c: ExtPhoneChannel }) {
  const [word, tone] = c.bridge ? BRIDGE[c.bridge.state] : ["нет", "text-fg-faint"];
  return (
    <div className="flex flex-col pl-4">
      <span className="text-fg-muted" title="последний наш ответ клиенту в чатах этого канала — откуда бы ни отвечали">
        наш ответ:{" "}
        {c.replied ? (
          <span className={replying(c) ? "text-fg" : ""} title={when(c.replied.at)}>
            {ago(c.replied.age)}
            {c.replied.author && ` · ${c.replied.author}`}
          </span>
        ) : (
          <span className="text-fg-faint">нет за сутки</span>
        )}
      </span>
      <span className="text-fg-muted" title={c.bridge ? `мост в служебном браузере ${c.bridge.name}` : "вкладки моста у этого канала нет"}>
        мост: <span className={tone}>{word}</span>
        {c.bridge?.state === "silent" && c.bridge.age !== null && <span className="text-err"> {ago(c.bridge.age)}</span>}
      </span>
    </div>
  );
}

/** Используем ли канал номера; пока запрос идёт — показывает то, что нажали. */
function UseSwitch({ on, label, save }: { on: boolean; label: string; save: (use: boolean) => Promise<unknown> }) {
  const client = useQueryClient();
  const set = useMutation({ mutationFn: save, onSettled: () => client.invalidateQueries({ queryKey: ["ext-status"] }) });
  const now = set.isPending ? !!set.variables : on;
  return (
    <span className="inline-flex items-center gap-2 pl-4">
      <button
        type="button"
        role="switch"
        aria-checked={now}
        aria-label={label}
        title="Канал не используем (номер на площадке заблокирован, аккаунта нет) — он не считается «не онлайн», и ответа клиентам на нём не ждём"
        disabled={set.isPending}
        onClick={() => set.mutate(!now)}
        className={`relative h-[16px] w-[28px] shrink-0 rounded-full transition-colors disabled:opacity-60 ${now ? "bg-ok" : "bg-fg-faint"}`}
      >
        <span className={`absolute top-[2px] h-[12px] w-[12px] rounded-full bg-white transition-[left] ${now ? "left-[14px]" : "left-[2px]"}`} />
      </button>
      <span className="text-fg-faint">канал используем</span>
      {set.isError && <span className="text-err">{set.error.message}</span>}
    </span>
  );
}

/**
 * `toggle` — переключатель «канал используем» под ячейкой (только тем, кому положено); без него
 * ячейка только показывает.
 */
export function ChannelCell({ c, toggle }: { c: ExtPhoneChannel; toggle?: ((off: boolean) => Promise<unknown>) | undefined }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Waiting w={c.waiting} />
      {!c.off && <BridgeDown b={c.bridge} />}
      {c.off ? (
        <div className="flex items-baseline gap-2">
          <Dot tone="off" />
          <div className="flex flex-col">
            <span className="text-fg-muted">не используем</span>
            <span className="text-fg-faint">
              отметил: {c.off.name || "—"} · {when(c.off.at)}
            </span>
          </div>
        </div>
      ) : (
        <Presence c={c} />
      )}
      <Facts c={c} />
      {toggle && <UseSwitch on={!c.off} label={`Используем ${c.platform}`} save={(use) => toggle(!use)} />}
    </div>
  );
}

// В смене — статус учёта времени CRM «работает»; CRM статуса не отдала — считаем, что работает.
const working = (u: { status: ExtTimeStatus | null }) => !u.status || u.status.kind === "work";

/**
 * Кто на канале. Открытая вкладка — ещё не «работает»: вкладку держат и те, кто ушёл со смены, а
 * сотрудник в смене может отвечать из вкладки, которая отметок не шлёт. Поэтому первым называем
 * того, кто в смене по учёту времени CRM: на этом канале, иначе — на этом же номере в другом канале.
 */
function Presence({ c }: { c: ExtPhoneChannel }) {
  const here = c.users.filter(working); // в смене и канал открыт
  const idle = c.users.filter((u) => !working(u)); // канал открыт, но не в смене
  const beside = (c.beside ?? []).filter(working); // в смене, держит этот номер в другом канале
  const tone = here.length ? "ok" : beside.length || replying(c) ? "warn" : c.users.length || c.last ? "err" : "off";
  return (
    <div className="flex items-baseline gap-2">
      <Dot tone={tone} />
      <div className="flex flex-col gap-1.5">
        {here.map((u) => (
          <div key={u.id} className="flex flex-col">
            <span>
              <span className="font-medium">{u.name || `#${u.id}`}</span>
              <span className="text-fg-faint"> · {u.visible ? "на виду" : "в фоне"}</span>
            </span>
            <TimeStatus status={u.status} />
            <Version of={u} />
            <Errors n={u.errors} />
            <Deeds of={u} />
          </div>
        ))}
        {here.length === 0 &&
          beside.length === 0 &&
          (replying(c) ? (
            <span className="text-warn" title="Вкладки с расширением у сотрудника в смене нет, но в чатах канала отвечают: с телефона, из приложения или из браузера без расширения">
              {c.users.length ? "в смене никого, но отвечают" : c.last ? "вкладка не открыта, но отвечают" : "вкладку не открывали, но отвечают"}
            </span>
          ) : c.users.length ? (
            <span className="text-err">в смене никого</span>
          ) : c.last ? (
            <span className="text-err">не онлайн</span>
          ) : (
            <span className="text-fg-faint">не открывали</span>
          ))}
        {beside.map((u) => (
          <div key={u.id} className="flex flex-col">
            <span>
              <span className="text-fg-muted">{here.length ? "также на номере: " : "в смене на номере: "}</span>
              <span className={here.length ? "text-fg" : "font-medium"}>{u.name || `#${u.id}`}</span>
              <span className="text-fg-faint"> · открыт {u.platforms.join(", ")}</span>
            </span>
            {here.length === 0 && <TimeStatus status={u.status} />}
            <Version of={u} />
            {here.length === 0 && <Silent platform={c.platform} of={u} />}
            <Errors n={u.errors} />
          </div>
        ))}
        {idle.map((u) => (
          <div key={u.id} className="flex flex-col text-fg-muted">
            <span title="вкладка канала открыта в браузере сотрудника, но по учёту времени CRM он не в смене">
              вкладка открыта у: {u.name || `#${u.id}`} · {u.visible ? "на виду" : "в фоне"}
            </span>
            <TimeStatus status={u.status} />
            <Version of={u} />
            <Deeds of={u} />
          </div>
        ))}
        {c.users.length === 0 && c.last && (
          <div className="flex flex-col">
            <span className="text-fg-muted" title={`последняя отметка вкладки ${c.platform} с расширением: ${when(c.last.seen)}`}>
              последняя отметка: {c.last.name || `#${c.last.id}`} · {ago(c.last.age)}
            </span>
            {/* Он же назван выше со статусом — дважды не пишем. */}
            {!beside.some((u) => u.id === c.last?.id) && <TimeStatus status={c.last.status} />}
            <Version of={c.last} />
            <Deeds of={c.last} />
          </div>
        )}
      </div>
    </div>
  );
}
